/**
 * The motion rule, written once for both ends.
 *
 * There are three parties to how animated this app is — the `motion_pref`
 * column on the user row, the device's own `prefers-reduced-motion`, and the
 * `data-motion` attribute on `<html>` that the stylesheet keys off — and
 * exactly one question worth getting right between them: *is this screen
 * moving or not?* That question is answered here, and `server/motion.ts` and
 * `src/motion.ts` both import the answer rather than each writing it, so the
 * server and the browser cannot disagree about what somebody is looking at.
 * It is the same arrangement `src/lib/theme.ts` makes, for the same reason.
 *
 * ## Why a column and not just the media query
 *
 * `prefers-reduced-motion` is a fact about a device, and it is the right
 * default — but it is set in an operating system's accessibility panel, and
 * plenty of people who would rather this app sat still have never been in
 * there. An animation that cannot be turned off is not a fun one, so the
 * preference is also a control, on the user row, that overrides the device in
 * *either* direction:
 *
 *   - `system`  — the device decides. What every account starts on.
 *   - `reduced` — calm, on a phone that is not set to reduce motion.
 *   - `full`    — animated, on a phone that is. This is why it is not a
 *                 checkbox: a two-state "reduce motion" box on a device that
 *                 already reduces motion is a control that cannot do
 *                 anything, and a control that does nothing is worse than no
 *                 control at all.
 *
 * ## The two numbers the ticket names
 *
 * `STAGGER_RISE_PX` and `STAGGER_MS` are the 22px and the 85ms a list's rows
 * arrive with. They are stated here *and* in `src/index.css` as `--rise` and
 * `--stagger`, because the keyframe needs them and this module needs them to
 * be checkable; `tests/motion-client.test.ts` reads the stylesheet and fails
 * if the two copies stop saying the same thing, exactly as
 * `tests/theme-client.test.ts` does for the theme key in `index.html`.
 */

/** The three answers `users.motion_pref` is allowed to hold. */
export const MOTION_PREFERENCES = ['system', 'full', 'reduced'] as const

export type MotionPreference = (typeof MOTION_PREFERENCES)[number]

/** What a device, or an override, resolves to. There is no third state. */
export type MotionState = 'full' | 'reduced'

/** What every account starts on: whatever the device already says. */
export const DEFAULT_MOTION_PREFERENCE: MotionPreference = 'system'

/** The attribute the stylesheet keys off, on `<html>`. Holds a `MotionState`. */
export const MOTION_ATTRIBUTE = 'data-motion'

/**
 * Where the browser remembers the *resolved* state, so the first paint of the
 * next load is already right. The inline script in `index.html` reads this key
 * by its literal text; `tests/motion-client.test.ts` checks the two agree.
 */
export const MOTION_STORAGE_KEY = 'friendloan.motion'

export function isMotionPreference(value: unknown): value is MotionPreference {
  return typeof value === 'string' && (MOTION_PREFERENCES as readonly string[]).includes(value)
}

/**
 * A `users.motion_pref` value as one of the three, whatever is actually in the
 * column. A row written by a build that knew a fourth word — or by hand —
 * reads as the default rather than as a crash, for the same reason
 * `effectiveThemeId` answers an unknown theme with the default one.
 */
export function motionPreferenceOf(value: unknown): MotionPreference {
  return isMotionPreference(value) ? value : DEFAULT_MOTION_PREFERENCE
}

export function isMotionState(value: unknown): value is MotionState {
  return value === 'full' || value === 'reduced'
}

/**
 * **The whole rule.** A preference and a device, in; one of two answers, out.
 *
 * `system` is the only one that consults the device, which is what makes the
 * other two overrides rather than suggestions. Note which way round the
 * default falls: an account that has never touched the control and a device
 * that says nothing both land on `full`, and a device that asks for less
 * movement gets it without anybody having to find a setting.
 */
export function resolveMotion(
  preference: MotionPreference | null,
  deviceReducesMotion: boolean,
): MotionState {
  if (preference === 'reduced') return 'reduced'
  if (preference === 'full') return 'full'
  return deviceReducesMotion ? 'reduced' : 'full'
}

/** How the control describes each of its three positions. */
export const MOTION_LABELS: Record<MotionPreference, string> = {
  system: 'Match my device',
  full: 'Full motion',
  reduced: 'Reduced motion',
}

// ---------------------------------------------------------------------------
// The numbers
// ---------------------------------------------------------------------------

/** How long a number takes to count to its value. `--count` is not in the CSS:
 *  the counting is a rAF loop in `src/motion-react.ts`, and this is its only
 *  statement of the duration. */
export const COUNT_MS = 600

/** How far a row travels as it arrives. `--rise` in `src/index.css`. */
export const STAGGER_RISE_PX = 22

/** Between one row's arrival and the next. `--stagger` in `src/index.css`. */
export const STAGGER_MS = 85

/**
 * The most steps of delay any row gets.
 *
 * 85ms each is a pleasant ripple over eight rows and an eleven-second wait
 * over a hundred and thirty. So the delay stops climbing: the first dozen
 * rows stagger, and everything below the fold arrives with the twelfth,
 * which is the row somebody is still looking at when they get there anyway.
 */
export const STAGGER_MAX_STEPS = 12

/** Which step of the stagger row `index` is on. Never more than the cap. */
export function staggerStep(index: number): number {
  if (!Number.isFinite(index) || index <= 0) return 0
  return Math.min(Math.floor(index), STAGGER_MAX_STEPS)
}

/**
 * The easing the ticket asks for: fast away from where it was, and a long
 * settle onto the number it is going to. `1 - 2^(-10t)`, clamped at both ends
 * so that `t = 1` is exactly 1 rather than 0.999.
 */
export function easeOutExpo(t: number): number {
  if (t <= 0) return 0
  if (t >= 1) return 1
  return 1 - 2 ** (-10 * t)
}

/**
 * What a counting number reads at `elapsedMs`, in whole cents.
 *
 * Integers in, integers out — the same rule the rest of this app's money
 * follows. It rounds the *difference* rather than the value so that the last
 * frame is the target exactly and not a cent off it, and any elapsed time at
 * or past the duration is the target itself, so a tab that was backgrounded
 * for a second lands on the right number rather than somewhere along the
 * curve.
 */
export function countAt(
  from: number,
  to: number,
  elapsedMs: number,
  durationMs: number = COUNT_MS,
): number {
  if (durationMs <= 0 || elapsedMs >= durationMs) return to
  if (elapsedMs <= 0) return from
  return from + Math.round((to - from) * easeOutExpo(elapsedMs / durationMs))
}

/**
 * Whether counting from `from` to `to` is somebody being settled up.
 *
 * The one animation in this app that is a celebration rather than a
 * transition, so it is worth being exact about when it happens: a balance
 * that *arrives* at zero, from either side. A row that was already at zero is
 * not settling — nothing happened — and neither is one on its first paint
 * with nothing owed, which is every stranger's home screen and would be a
 * confetti burst about nothing.
 */
export function isSettling(from: number, to: number): boolean {
  return to === 0 && from !== 0
}

/**
 * How long the settled celebration lasts — the flash, and the confetti's whole
 * flight. `--settle` in `src/index.css` matches, and
 * `tests/motion-client.test.ts` holds them together.
 *
 * It is longer than `COUNT_MS` on purpose: the burst starts with the count and
 * is still in the air when the number arrives at zero, which is the order the
 * two things actually happen in.
 */
export const SETTLE_MS = 1200
